import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

// APIs
import {
  getIndividualScaleDevice,
  assignScaleDeviceToStore,
} from '../../../api/admin/scaleDevices/scaleDevices';
import { getAllStores } from '../../../api/admin/stores/stores';

// Components
import DataCard from '../../common/card/DataCard';
import Modal from '../../common/Modal';
import IndividualValueEditForm from '../../common/forms/IndividualValueEditForm';
import CentsLoader from '../../common/centsLoader/CentsLoader';

// Utils
import { formatStoreDisplayName } from 'utils/formatters/formatStoreDisplayName';

const IndividualScaleDevice = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState();
  const [scaleDevice, setScaleDevice] = useState();
  const [stores, setStores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [chosenStore, setChosenStore] = useState(null);
  const [modalType, setModalType] = useState();

  useEffect(() => {
    fetchIndividualScaleDevice();
    fetchAllStores();
    // eslint-disable-next-line
  }, []);

  /**
   * Reteieve an individual ScaleDevice from our backend
   */
  const fetchIndividualScaleDevice = async () => {
    try {
      setLoading(true);

      const deviceData = await getIndividualScaleDevice(id);
      setScaleDevice(deviceData.data.scaleDevice);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.response.data.error);
    }
  };

  /**
   * Fetch all stores for a scale to be attached to
   */
  const fetchAllStores = async () => {
    try {
      const storeData = await getAllStores();
      const mappedStores = storeData.data.stores.map((store) => ({
        display: formatStoreDisplayName(store.name, store.id),
        value: store.id,
      }));
      setStores(mappedStores);
    } catch (error) {
      setErrorMessage(error.response.data.error);
    }
  };

  /**
   * Render the core store details
   */
  const renderScaleDetails = () => {
    const scaleDetails = [
      {
        label: 'Pin Number',
        data: scaleDevice.pinNumber,
      },
      {
        label: 'Device UUID',
        data: scaleDevice.deviceUuid,
      },
      {
        label: 'Store Name',
        data: scaleDevice.store
          ? formatStoreDisplayName(scaleDevice.store.name, scaleDevice.store.id)
          : '--',
        editable: true,
      },
    ];

    return (
      <DataCard
        title={'Scale Device Details'}
        cardData={scaleDetails}
        onEdit={(modalType) => {
          setModalType(modalType);
          setShowModal(true);
        }}
      />
    );
  };

  /**
   * Open a modal that assigns a ScaleDevice to a Store.
   *
   * Modal should contain:
   *
   * 1) current value for the given modal type
   * 2) options and/or input for updated value
   * 3) save button to update the ScaleDevice
   *
   * @param {String} modalType
   */
  const renderModal = () => {
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowModal(false);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={
            scaleDevice.store
              ? formatStoreDisplayName(scaleDevice.store.name, scaleDevice.store.id)
              : '--'
          }
          updatedValue={chosenStore}
          handleValueChange={(event) => {
            handleStoreChange(event);
          }}
          inputType="select"
          selectOptions={stores}
          onSave={() => {
            attachDeviceToStore();
          }}
          error={errorMessage}
        />
      </Modal>
    );
  };

  /**
   * Update status based on new selection or selection changes
   *
   * @param {Object} event
   */
  const handleStoreChange = (event) => {
    setErrorMessage(null);
    setChosenStore(event.value);
  };

  /**
   * Assign a specific ScaleDevice to a specific Store
   */
  const attachDeviceToStore = async () => {
    try {
      setLoading(true);

      const payload = {
        storeId: chosenStore,
      };

      const scaleDeviceData = await assignScaleDeviceToStore(id, payload);
      setScaleDevice(scaleDeviceData.data.scaleDevice);

      setShowModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.response.data.error);
    }
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="page-container">
          <div className="scale-page-container">
            <h1 className="scale-header">ScaleDevice ID: {scaleDevice.id}</h1>
            {renderScaleDetails()}
          </div>
          {showModal && renderModal()}
        </div>
      )}
    </>
  );
};

export default IndividualScaleDevice;
